import googlePlacesAddressNormalizer from 'google-places-address-normalizer'
import { compose, withHandlers, withState } from 'recompose'
import { fromEvent } from 'rxjs'
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators'

import withGooglePlaces from '../../screens/signup/withGooglePlaces'
import { AutocompletePrediction, HandlerProps, InnerProps } from './types'

const withAddressAutocomplete = compose<InnerProps, {}>(
  withGooglePlaces,
  withState('subscription', 'setSubscription', null),
  withState('autoCompleteTime', 'setAutoCompleteTime', Date.now()),
  withState(
    'addressAutoCompleteOptions',
    'setAddressAutoCompleteOptions',
    null,
  ),
  withHandlers<InnerProps, Partial<HandlerProps>>({
    handleDetailsResults:
      ({ setFieldValue }) =>
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const normalizedAddress = googlePlacesAddressNormalizer(
            place.address_components,
          )
          setFieldValue('address1', normalizedAddress.address || '')
          setFieldValue('city', normalizedAddress.city || '')
          setFieldValue('state', normalizedAddress.state_abbreviation || '')
          setFieldValue('zip', normalizedAddress.postal_code || '')
        }
      },
    handleClickOutside:
      ({ setAddressAutoCompleteOptions }) =>
      () => {
        setAddressAutoCompleteOptions(null)
      },
    handleStreamChange:
      ({ autoComplete, autoCompleteTime, setAddressAutoCompleteOptions }) =>
      (address) => {
        if (address.length === 0) {
          setAddressAutoCompleteOptions(null)
        } else if (
          address.length >= 4 &&
          Date.now() - autoCompleteTime >= 1000
        ) {
          autoComplete.getPlacePredictions(
            {
              componentRestrictions: {
                country: 'us',
              },
              input: address,
              types: ['address'],
            },
            (data: AutocompletePrediction[]) => {
              setAddressAutoCompleteOptions(data)
            },
          )
        }
      },
  }),
  withHandlers<Partial<InnerProps>, Partial<HandlerProps>>({
    handleAutoCompleteSelect:
      ({
        handleDetailsResults,
        placesService,
        setAddressAutoCompleteOptions,
        setAutoCompleteTime,
      }) =>
      (suggestion: AutocompletePrediction) => {
        setAddressAutoCompleteOptions(null)
        setAutoCompleteTime(Date.now())
        // Note: for fetching zip code
        placesService.getDetails(
          {
            placeId: suggestion.place_id,
            fields: ['address_component'],
          },
          handleDetailsResults,
        )
      },
    setupObservable:
      ({ handleStreamChange, subscription, setSubscription }) =>
      (input: HTMLInputElement) => {
        if (input && !subscription) {
          setSubscription(
            fromEvent(input, 'input')
              .pipe(
                map(
                  (event: React.ChangeEvent<HTMLInputElement>) =>
                    event.target.value,
                ),
                debounceTime(250),
                distinctUntilChanged(),
              )
              .subscribe(handleStreamChange),
          )
        }
      },
  }),
)

export default withAddressAutocomplete
